import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { financialService } from '../../../services/api';

const FinancialCharts = ({ selectedPeriod, selectedHotel }) => {
  const [activeChart, setActiveChart] = useState('revenue');
  const [viewMode, setViewMode] = useState('monthly'); // 'daily' ou 'monthly'
  const [chartData, setChartData] = useState({
    revenueData: [],
    expenseData: [],
    profitTrendData: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadChartData();
  }, [selectedPeriod, selectedHotel, viewMode]);

  const loadChartData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {};
      if (selectedHotel?.id) {
        params.hotelId = selectedHotel.id;
      }

      // Definir período baseado no modo de visualização
      if (viewMode === 'daily') {
        params.period = '30d'; // Últimos 30 dias
      } else {
        params.period = '6m'; // Últimos 6 meses
      }

      // Buscar dados dos gráficos
      const [chartsResponse, expenseCategoriesResponse] = await Promise.all([
        financialService.getCharts(params),
        financialService.getExpenseCategories(params)
      ]);

      // Processar dados de receita
      const revenueData = chartsResponse.map(item => ({
        month: item.month,
        receita: item.revenue,
        despesas: item.expenses,
        lucro: item.profit
      }));

      // Processar dados de despesas por categoria
      const colors = ['#1E40AF', '#0F766E', '#DC2626', '#D97706', '#059669', '#7C3AED', '#BE185D'];
      const expenseData = expenseCategoriesResponse.map((item, index) => ({
        category: item.label,
        value: item.amount,
        color: colors[index % colors.length]
      }));

      // Processar dados de margem de lucro
      const profitTrendData = chartsResponse.map(item => ({
        month: item.month,
        margem: item.revenue > 0 ? ((item.profit / item.revenue) * 100).toFixed(1) : 0
      }));

      setChartData({
        revenueData,
        expenseData,
        profitTrendData
      });
    } catch (err) {
      console.error('Erro ao carregar dados dos gráficos:', err);
      setError('Erro ao carregar dados dos gráficos');
    } finally {
      setLoading(false);
    }
  };

  const chartTabs = [
    { id: 'revenue', label: 'Receita vs Despesas', icon: 'BarChart3' },
    { id: 'expenses', label: 'Categorias de Despesas', icon: 'PieChart' },
    { id: 'profit', label: 'Margem de Lucro', icon: 'TrendingUp' }
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="font-semibold text-foreground mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.dataKey}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="font-semibold text-foreground">{data.name}</p>
          <p className="text-sm text-muted-foreground">
            {formatCurrency(data.value)} ({((data.value / chartData.expenseData.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(1)}%)
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
        <div className="animate-pulse">
          <div className="h-6 bg-muted rounded w-1/4 mb-4"></div>
          <div className="h-64 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
        <div className="flex items-center space-x-3 text-destructive">
          <Icon name="AlertCircle" size={20} />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const renderChart = () => {
    switch (activeChart) {
      case 'revenue':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData.revenueData} margin={{ top: 20, right: 30, left: 20, bottom: viewMode === 'daily' ? 60 : 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="month" 
                stroke="#6b7280"
                interval={viewMode === 'daily' ? 'preserveStartEnd' : 0}
                tick={{ fontSize: 12 }}
                angle={viewMode === 'daily' ? -45 : 0}
                textAnchor={viewMode === 'daily' ? 'end' : 'middle'}
                height={viewMode === 'daily' ? 60 : 30}
              />
              <YAxis stroke="#6b7280" tickFormatter={formatCurrency} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="receita" fill="#3b82f6" name="Receita" radius={[4, 4, 0, 0]} />
              <Bar dataKey="despesas" fill="#ef4444" name="Despesas" radius={[4, 4, 0, 0]} />
              <Bar dataKey="lucro" fill="#10b981" name="Lucro" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'expenses':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={chartData.expenseData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ category, percent }) => `${category} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {chartData.expenseData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<PieTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        );

      case 'profit':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData.profitTrendData} margin={{ top: 20, right: 30, left: 20, bottom: viewMode === 'daily' ? 60 : 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="month" 
                stroke="#6b7280"
                interval={viewMode === 'daily' ? 'preserveStartEnd' : 0}
                tick={{ fontSize: 12 }}
                angle={viewMode === 'daily' ? -45 : 0}
                textAnchor={viewMode === 'daily' ? 'end' : 'middle'}
                height={viewMode === 'daily' ? 60 : 30}
              />
              <YAxis stroke="#6b7280" />
              <Tooltip content={<CustomTooltip />} />
              <Line 
                type="monotone" 
                dataKey="margem" 
                stroke="#10b981" 
                strokeWidth={2}
                name="Margem (%)"
                dot={{ fill: '#10b981', r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
      {/* Header with title and period selector */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6 pb-4 border-b border-border">
        <div className="flex-1">
          <h3 className="text-xl font-bold text-foreground mb-1">Análise Financeira</h3>
          <p className="text-sm text-muted-foreground">
            {viewMode === 'daily' ? '📊 Últimos 30 dias - Visualização diária' : '📈 Últimos 6 meses - Visualização mensal'}
          </p>
        </div>
        
        {/* Period Toggle */}
        <div className="flex items-center gap-2 bg-muted/30 p-1 rounded-lg">
          <button
            onClick={() => setViewMode('daily')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
              viewMode === 'daily'
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            }`}
          >
            <Icon name="Calendar" size={16} />
            <span>Diário</span>
          </button>
          <button
            onClick={() => setViewMode('monthly')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
              viewMode === 'monthly'
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            }`}
          >
            <Icon name="CalendarRange" size={16} />
            <span>Mensal</span>
          </button>
        </div>
      </div>

      {/* Chart Type Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {chartTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveChart(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeChart === tab.id
                ? 'bg-primary text-primary-foreground shadow-md'
                : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
          >
            <Icon name={tab.icon} size={18} />
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Chart Container */}
      <div className="relative bg-muted/20 rounded-lg p-4">
        {chartData.revenueData.length === 0 ? (
          <div className="flex items-center justify-center h-80 text-muted-foreground">
            <div className="text-center">
              <Icon name="BarChart3" size={56} className="mx-auto mb-4 opacity-30" />
              <p className="text-lg font-medium mb-1">Sem dados disponíveis</p>
              <p className="text-sm">Nenhum dado financeiro para o período selecionado</p>
            </div>
          </div>
        ) : (
          <div className="h-96">
            {renderChart()}
          </div>
        )}
      </div>
    </div>
  );
};

export default FinancialCharts;